import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useChildren } from "@/hooks/useChildren";
import { useAuth } from "@/hooks/useAuth";
import { fireEvent } from "@/lib/events";

export type FeatureKey =
  | "drawing"
  | "collections"
  | "drawing_limit"
  | "capsule_limit"
  | "storage_full"
  | "photos"
  | "trip"
  | "time_capsule"
  | "voice_context"
  | "voice_cap"
  | "video_cap"
  | "custom_collections"
  | "active_capture"
  | "premium_template"
  | "child_creator"
  | "member_cap"
  | "contributor_cap";

interface LockedFeatureSheetProps {
  feature: FeatureKey;
  open: boolean;
  onClose: () => void;
  childName?: string;
}

interface FeatureCopy {
  emoji: string;
  title: string;
  body: string;
  tier: string;
  cta: string;
  secondaryCta?: string;
  note?: string;
  comingSoon?: boolean;
}

const LockedFeatureSheet = ({
  feature,
  open,
  onClose,
  childName: childNameProp,
}: LockedFeatureSheetProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeChild } = useChildren();
  const { user } = useAuth();
  const childName = childNameProp ?? activeChild?.name ?? t("common.all");

  // Build copy inside component so t() works
  const FEATURE_COPY: Record<FeatureKey, FeatureCopy> = {
    drawing: {
      emoji: "🎨",
      title: t("lockedFeature.drawing.title"),
      body: t("lockedFeature.drawing.body", { name: childName }),
      tier: t("lockedFeature.drawing.tier"),
      cta: t("lockedFeature.drawing.cta"),
    },
    collections: {
      emoji: "🗺️",
      title: t("lockedFeature.collections.title"),
      body: t("lockedFeature.collections.body", { name: childName }),
      tier: t("lockedFeature.collections.tier"),
      cta: t("lockedFeature.collections.cta"),
    },
    drawing_limit: {
      emoji: "🎨",
      title: t("lockedFeature.drawingLimit.title"),
      body: t("lockedFeature.drawingLimit.body"),
      tier: t("lockedFeature.drawingLimit.tier"),
      cta: t("lockedFeature.drawingLimit.cta"),
    },
    capsule_limit: {
      emoji: "🕰️",
      title: t("lockedFeature.capsuleLimit.title"),
      body: t("lockedFeature.capsuleLimit.body"),
      tier: t("lockedFeature.capsuleLimit.tier"),
      cta: t("lockedFeature.capsuleLimit.cta"),
    },
    storage_full: {
      emoji: "🔥",
      title: t("lockedFeature.storageFull.title"),
      body: t("lockedFeature.storageFull.body"),
      tier: t("lockedFeature.storageFull.tier"),
      cta: t("lockedFeature.storageFull.cta"),
      note: t("lockedFeature.storageFull.note"),
    },
    photos: {
      emoji: "📸",
      title: t("lockedFeature.photos.title"),
      body: t("lockedFeature.photos.body"),
      tier: t("lockedFeature.photos.tier"),
      cta: t("lockedFeature.photos.cta"),
    },
    trip: {
      emoji: "🗺️",
      title: t("lockedFeature.trip.title"),
      body: t("lockedFeature.trip.body", { name: childName }),
      tier: t("lockedFeature.trip.tier"),
      cta: t("lockedFeature.trip.cta"),
    },
    time_capsule: {
      emoji: "🕰️",
      title: t("lockedFeature.timeCapsule.title"),
      body: t("lockedFeature.timeCapsule.body"),
      tier: t("lockedFeature.timeCapsule.tier"),
      cta: t("lockedFeature.timeCapsule.cta"),
      comingSoon: true,
    },
    voice_context: {
      emoji: "🎤",
      title: t("lockedFeature.voiceContext.title"),
      body: t("lockedFeature.voiceContext.body", { name: childName }),
      tier: t("lockedFeature.voiceContext.tier"),
      cta: t("lockedFeature.voiceContext.cta"),
    },
    voice_cap: {
      emoji: "📼",
      title: t("lockedFeature.voiceCap.title"),
      body: t("lockedFeature.voiceCap.body"),
      tier: t("lockedFeature.voiceCap.tier"),
      cta: t("lockedFeature.voiceCap.cta"),
    },
    video_cap: {
      emoji: "🎬",
      title: t("lockedFeature.videoCap.title"),
      body: t("lockedFeature.videoCap.body"),
      tier: t("lockedFeature.videoCap.tier"),
      cta: t("lockedFeature.videoCap.cta"),
      comingSoon: true,
    },
    custom_collections: {
      emoji: "✨",
      title: t("lockedFeature.customCollections.title"),
      body: t("lockedFeature.customCollections.body"),
      tier: t("lockedFeature.customCollections.tier"),
      cta: t("lockedFeature.customCollections.cta"),
    },
    active_capture: {
      emoji: "🎯",
      title: t("lockedFeature.activeCapture.title"),
      body: t("lockedFeature.activeCapture.body"),
      tier: t("lockedFeature.activeCapture.tier"),
      cta: t("lockedFeature.activeCapture.cta"),
    },
    premium_template: {
      emoji: "🌟",
      title: t("lockedFeature.premiumTemplate.title"),
      body: t("lockedFeature.premiumTemplate.body"),
      tier: t("lockedFeature.premiumTemplate.tier"),
      cta: t("lockedFeature.premiumTemplate.cta"),
    },
    child_creator: {
      emoji: "🧒",
      title: t("lockedFeature.childCreator.title", { name: childName }),
      body: t("lockedFeature.childCreator.body", { name: childName }),
      tier: t("lockedFeature.childCreator.tier"),
      cta: t("lockedFeature.childCreator.cta"),
    },
    member_cap: {
      emoji: "👨‍👩‍👦",
      title: t("lockedFeature.memberCap.title"),
      body: t("lockedFeature.memberCap.body"),
      tier: t("lockedFeature.memberCap.tier"),
      cta: t("lockedFeature.memberCap.cta"),
    },
    contributor_cap: {
      emoji: "🎙️",
      title: t("lockedFeature.contributorCap.title"),
      body: t("lockedFeature.contributorCap.body"),
      tier: t("lockedFeature.contributorCap.tier"),
      cta: t("lockedFeature.contributorCap.cta"),
    },
  };

  const copy = FEATURE_COPY[feature];

  const handlePrimaryCta = () => {
    if (copy.comingSoon) {
      onClose();
      return;
    }
    if (user) {
      fireEvent("upsell.card.tapped", {
        userId: user.id,
        cardId: `locked-${feature}`,
        tier: "keeper",
      });
    }
    onClose();
    navigate("/upgrade");
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="bottom"
        className="z-[60] rounded-t-3xl px-6 pt-6 pb-10"
        overlayClassName="z-[60]"
      >
        <SheetTitle className="sr-only">{copy.title}</SheetTitle>

        <div className="flex flex-col items-center gap-4 text-center">
          <span style={{ fontSize: 32, lineHeight: 1 }}>{copy.emoji}</span>

          <h2
            className="text-foreground"
            style={{ fontSize: 15, fontWeight: 500 }}
          >
            {copy.title}
          </h2>

          <p
            className="text-muted-foreground max-w-[280px]"
            style={{ fontSize: 13, lineHeight: 1.5 }}
          >
            {copy.body}
          </p>

          <p
            className="text-muted-foreground/70"
            style={{ fontSize: 11 }}
          >
            {copy.tier}
          </p>

          {copy.note && (
            <div
              className="rounded-lg px-3 py-2 w-full"
              style={{
                backgroundColor: "#FDF0EE",
                color: "#8B3A2E",
                fontSize: 11,
              }}
            >
              {copy.note}
            </div>
          )}

          <div className="flex flex-col gap-2 w-full pt-1">
            <Button onClick={handlePrimaryCta} className="w-full h-11">
              {copy.cta}
            </Button>

            {copy.secondaryCta && (
              <Button
                variant="outline"
                onClick={() => {
                  if (user) {
                    fireEvent("upsell.card.tapped", {
                      userId: user.id,
                      cardId: `locked-${feature}-secondary`,
                      tier: "keeper",
                    });
                  }
                  onClose();
                  navigate("/upgrade");
                }}
                className="w-full h-11"
              >
                {copy.secondaryCta}
              </Button>
            )}

            <button
              type="button"
              onClick={onClose}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors py-2"
            >
              {t("lockedFeature.maybeLater")}
            </button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default LockedFeatureSheet;
